import { createContext, ReactNode, useContext, useEffect, useState, SetStateAction, Dispatch } from 'react';

import Logger from '@/utils/CapcitorLog';
import { SystemConfig } from '@/utils/getFirebaseSystemConfigByConfigId';

import { useDatabase } from '@/hooks/useDatabase';

interface SystemConfigContextType {
  setConfigId: Dispatch<SetStateAction<string | null | undefined | 'Undefined'>>;
  setConfig: Dispatch<SetStateAction<SystemConfig | null>>;
  config: SystemConfig | null;
}

export const SystemConfigContext = createContext<SystemConfigContextType>({
  setConfigId: () => undefined,
  setConfig: () => undefined,
  config: null,
});

export const SystemConfigProvider = ({ children }: { children: ReactNode }) => {
  const [configId, setConfigId] = useState<string | null | undefined | 'Undefined'>('');
  const [config, setConfig] = useState<SystemConfig | null>(null);
  const { getFirebaseSystemConfigByConfigId } = useDatabase();

  useEffect(() => {
    async function getConfig() {
      if (configId && configId?.length > 0 && configId !== 'Undefined') {
        const config = await getFirebaseSystemConfigByConfigId(configId);

        if (config) {
          Logger.info('Config loaded:', config);
          setConfig(config);
        }
      }
    }

    getConfig();
  }, [configId]);

  return (
    <SystemConfigContext.Provider
      value={{
        setConfigId,
        setConfig,
        config,
      }}>
      {children}
    </SystemConfigContext.Provider>
  );
};

export const useSystemConfigContext = (): SystemConfigContextType => {
  const context = useContext(SystemConfigContext);

  if (!context) {
    throw new Error('useSystemConfigContext must be used within an SystemConfigProvider');
  }

  return context;
};
